/**
 * SeedDisplayScreen.js
 * Ecran d'affichage du code de recuperation BIP39 (24 mots) — PF-01 CP4.
 *
 * Contrat de securite (DEC-030, DEC-033) :
 *   - la phrase n'est QUE affichee, jamais persistee ni journalisee ;
 *   - aucun bouton Copier (Q2), aucun telechargement de fichier (Q3) ;
 *   - seule sortie possible : impression navigateur, feuille epuree.
 * Le composant ne rend que son contenu : le parent fournit la mise en page
 * (ProgressBar, conteneur plein ecran).
 */

import React, { useState } from 'react';
import { G, BtnPrimary, BtnBack, IconFrame, IconKey } from './onboardingAtoms';

const PRINT_STYLE = `
  @media print {
    body { background: #fff !important; }
    .sh-noprint { display: none !important; }
    .sh-print-header { display: block !important; }
    .sh-seed-cell {
      background: #fff !important;
      border: 1px solid #333 !important;
      color: #000 !important;
      break-inside: avoid;
    }
    .sh-seed-index { color: #555 !important; }
    .sh-seed-word  { color: #000 !important; }
    .sh-seed-title { color: #000 !important; }
  }
`;

// Stack systeme uniquement : zero @font-face, zero CDN, aucun appel reseau
// tiers depuis l'ecran qui affiche la seed (RGPD + surface d'attaque).
const MONO = "ui-monospace, 'SF Mono', 'Monaco', 'Cascadia Mono', 'Segoe UI Mono', 'Roboto Mono', 'Menlo', 'Consolas', monospace";

const IconAlert = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconPrinter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
    <rect x="6" y="14" width="12" height="8"/>
  </svg>
);

// Rend la seed non selectionnable a la souris et neutralise copie, coupe et
// menu contextuel (K100). Reste contournable via DevTools ou une capture
// d'ecran : l'objectif est de rendre la sortie numerique volontaire, pas
// impossible — meme raisonnement que l'ecran physique d'un Ledger.
const NO_SELECT = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
};

const blockClipboard = (e) => e.preventDefault();

const CLIPBOARD_GUARDS = {
  onCopy: blockClipboard,
  onCut: blockClipboard,
  onContextMenu: blockClipboard,
};

const DEFAULT_TITLE = 'Votre code de récupération';
const DEFAULT_INTRO =
  'Ces 24 mots sont la seule façon de retrouver votre coffre si vous oubliez ' +
  'votre mot de passe. Notez-les maintenant, sur papier, dans un endroit sûr. ' +
  'SafeHeir ne conserve aucune copie.';
const DEFAULT_PRINT_TITLE = 'SafeHeir — Code de récupération';
const DEFAULT_WARNING =
  'Ne prenez JAMAIS en photo. Ne stockez JAMAIS dans un cloud, une note ' +
  "numérique ou un email. Perdre ces mots = perdre l'accès à vos données.";
const DEFAULT_ACKNOWLEDGE_LABEL =
  "Je comprends que perdre ce code = perdre l'accès définitif à mes données. " +
  "SafeHeir ne pourra pas m'aider à le retrouver.";

// title / intro / printTitle / warning / acknowledgeLabel : textes optionnels
// pour réutiliser l'écran avec un autre code 24 mots (code de transmission
// PF-03). Défauts = PF-01.
const SeedDisplayScreen = ({
  seedPhrase,
  onContinue,
  onBack,
  title = DEFAULT_TITLE,
  intro = DEFAULT_INTRO,
  printTitle = DEFAULT_PRINT_TITLE,
  warning = DEFAULT_WARNING,
  acknowledgeLabel = DEFAULT_ACKNOWLEDGE_LABEL,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const words = seedPhrase.trim().split(/\s+/);

  return (
    <>
      <style>{PRINT_STYLE}</style>

      <div className="sh-noprint">
        <BtnBack onClick={onBack} />
        <IconFrame><IconKey /></IconFrame>
      </div>

      {/* En-tete reserve a la sortie papier. */}
      <div
        className="sh-print-header"
        style={{ display: 'none', marginBottom: 16, color: '#000' }}
      >
        <strong style={{ fontSize: 16 }}>{printTitle}</strong>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Généré le {new Date().toLocaleDateString('fr-FR')}
        </div>
      </div>

      <h2 className="sh-seed-title" style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
        {title}
      </h2>
      <p className="sh-noprint" style={{ margin: '0 0 1.75rem', color: G, fontSize: '0.9rem', lineHeight: 1.6 }}>
        {intro}
      </p>

      {/* Garde posee sur le conteneur ET sur chaque cellule : une selection
          glissee qui deborde de la grille reste neutralisee. */}
      <div
        {...CLIPBOARD_GUARDS}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          columnGap: 12,
          rowGap: 8,
          ...NO_SELECT,
        }}
      >
        {words.map((word, i) => (
          <div
            key={`${i}-${word}`}
            className="sh-seed-cell"
            {...CLIPBOARD_GUARDS}
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 8,
              padding: '10px 12px',
              fontFamily: MONO,
              fontSize: 15,
              lineHeight: 1.8,
              ...NO_SELECT,
            }}
          >
            <span className="sh-seed-index" style={{ color: '#445566' }}>
              {String(i + 1).padStart(2, '0')}.{' '}
            </span>
            <span className="sh-seed-word" style={{ color: '#ffffff' }}>{word}</span>
          </div>
        ))}
      </div>

      <div className="sh-noprint" style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: 'rgba(239,68,68,0.08)',
        border: '1px solid rgba(239,68,68,0.25)',
        borderRadius: 10,
        padding: '14px 16px',
        marginTop: 20,
        color: '#f87171',
      }}>
        <IconAlert />
        <span style={{ fontSize: 13, lineHeight: 1.5 }}>
          {warning}
        </span>
      </div>

      <div className="sh-noprint" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button
          type="button"
          onClick={() => window.print()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#ffffff',
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          <IconPrinter />
          Imprimer cette page
        </button>
      </div>

      <label
        className="sh-noprint"
        htmlFor="seed-acknowledged"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          marginTop: 20, cursor: 'pointer',
          color: '#E8ECF1', fontSize: 13, lineHeight: 1.5,
        }}
      >
        <input
          id="seed-acknowledged"
          name="seed-acknowledged"
          type="checkbox"
          checked={acknowledged}
          onChange={e => setAcknowledged(e.target.checked)}
          style={{
            appearance: 'none', WebkitAppearance: 'none',
            width: 18, height: 18, flexShrink: 0, marginTop: 1,
            borderRadius: 4, cursor: 'pointer',
            border: acknowledged ? '1.5px solid #00F5A0' : '1.5px solid rgba(255,255,255,0.3)',
            background: acknowledged
              ? "#00F5A0 url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230A1628' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E\") center / 12px no-repeat"
              : 'transparent',
          }}
        />
        <span>
          {acknowledgeLabel}
        </span>
      </label>

      <div className="sh-noprint" style={{ marginTop: 20 }}>
        <BtnPrimary onClick={onContinue} disabled={!acknowledged}>
          J'ai noté, continuer
        </BtnPrimary>
      </div>
    </>
  );
};

export default SeedDisplayScreen;
